import { activityItemMatchesIdentity } from './identity';
import {
    ACTIVITY_STAGES,
    isActivityBatchKey,
    type ActivityAvailability,
    type ActivityDismissalTombstoneMetadata,
    type ActivityIdentity,
    type ActivityJob,
    type ActivityKey,
    type ActivityStage,
} from './models';
import { MAX_STORED_JOBS_PER_ITEM, MAX_TOMBSTONES } from './storage-limits';

export function parseDismissalTombstoneMetadataArray(
    value: unknown
): readonly ActivityDismissalTombstoneMetadata[] | null {
    if (value === undefined) return [];
    if (!Array.isArray(value) || value.length > MAX_TOMBSTONES) return null;

    return parseArray(value, parseDismissalTombstoneMetadata);
}

function parseDismissalTombstoneMetadata(
    value: unknown
): ActivityDismissalTombstoneMetadata | null {
    if (!isRecord(value)) return null;

    const key = parseActivityKey(value['key']);
    const observedJobIdsValue = value['observedJobIds'];
    const latestObservedAtUtc = parseTimestamp(value['latestObservedAtUtc']);
    if (
        !key ||
        !isActivityBatchKey(key) ||
        !latestObservedAtUtc ||
        !Array.isArray(observedJobIdsValue) ||
        observedJobIdsValue.length === 0 ||
        observedJobIdsValue.length > MAX_STORED_JOBS_PER_ITEM
    ) {
        return null;
    }

    const observedJobIds = parseArray(
        observedJobIdsValue,
        parseCanonicalNonEmptyString
    );
    return !observedJobIds || hasDuplicates(observedJobIds)
        ? null
        : { key, observedJobIds, latestObservedAtUtc };
}

export function parseObservedJobIds(value: unknown): readonly string[] | null {
    if (
        !Array.isArray(value) ||
        value.length === 0 ||
        value.length > MAX_STORED_JOBS_PER_ITEM
    ) {
        return null;
    }

    const observedJobIds = parseArray(value, parseCanonicalNonEmptyString);
    return !observedJobIds || hasDuplicates(observedJobIds)
        ? null
        : observedJobIds;
}

export function parseIdentity(value: unknown): ActivityIdentity | null {
    if (!isRecord(value)) return null;

    const userId = parseNonEmptyString(value['userId']);
    const organisationId = parseNonEmptyString(value['organisationId']);
    return userId && organisationId ? { userId, organisationId } : null;
}

export function parseActivityKey(value: unknown): ActivityKey | null {
    return isActivityKey(value) ? value : null;
}

function isActivityKey(value: unknown): value is ActivityKey {
    if (typeof value !== 'string') return false;

    const [prefix, ...rest] = value.split(':');
    return (
        (prefix === 'batch' || prefix === 'job') &&
        rest.join(':').trim().length > 0
    );
}

export function keyMatchesActivityItem(
    key: ActivityKey,
    correlationId: string | null,
    jobs: readonly ActivityJob[]
): boolean {
    return activityItemMatchesIdentity(key, correlationId, jobs);
}

export function parseStage(value: unknown): ActivityStage | null {
    return typeof value === 'string' &&
        ACTIVITY_STAGES.includes(value as ActivityStage)
        ? (value as ActivityStage)
        : null;
}

export function parseStatus(value: unknown): ActivityJob['status'] | null {
    return value === 'Queued' ||
        value === 'Started' ||
        value === 'Progress' ||
        value === 'Completed' ||
        value === 'Failed' ||
        value === 'Skipped' ||
        value === 'Expired'
        ? value
        : null;
}

export function parseAvailability(value: unknown): ActivityAvailability | null {
    return value === 'available' || value === 'stale' || value === 'unavailable'
        ? value
        : null;
}

export function parsePercent(value: unknown): number | null | undefined {
    if (value === null) return null;
    return typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= 0 &&
        value <= 100
        ? value
        : undefined;
}

export function parseItemCounts(value: Readonly<Record<string, unknown>>):
    | {
          readonly completedItems: number | null;
          readonly totalItems: number | null;
      }
    | undefined {
    const completedItems = parseItemCount(value['completedItems']);
    const totalItems = parseItemCount(value['totalItems']);
    return completedItems !== undefined &&
        totalItems !== undefined &&
        (completedItems === null) === (totalItems === null) &&
        (completedItems === null ||
            totalItems === null ||
            completedItems <= totalItems)
        ? { completedItems, totalItems }
        : undefined;
}

function parseItemCount(value: unknown): number | null | undefined {
    if (value === undefined || value === null) return null;
    return typeof value === 'number' && Number.isInteger(value) && value >= 0
        ? value
        : undefined;
}

export function parseNullableNonEmptyString(
    value: unknown
): string | null | undefined {
    if (value === null) return null;
    return parseNonEmptyString(value) ?? undefined;
}

export function parseNonEmptyString(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function parseCanonicalNonEmptyString(value: unknown): string | null {
    const parsed = parseNonEmptyString(value);
    return parsed && parsed === parsed.trim() ? parsed : null;
}

export function parseTimestamp(value: unknown): string | null {
    return typeof value === 'string' && Number.isFinite(Date.parse(value))
        ? value
        : null;
}

export function parseArray<T>(
    values: readonly unknown[],
    parser: (value: unknown) => T | null
): readonly T[] | null {
    const parsed: T[] = [];
    for (const value of values) {
        const item = parser(value);
        if (item === null) return null;
        parsed.push(item);
    }
    return parsed;
}

export function hasDuplicates<T>(values: readonly T[]): boolean {
    return new Set(values).size !== values.length;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
